import { Badge } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { Grid } from '@kerfjs/ui/grid';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import type { SafeHtml } from 'kerfjs';
import { Lock } from 'lucide';

import type { QuoteMatrix } from '../../../data/api.js';
import { formatCurrencyText } from '../../../lib/currency-format.js';
/**
 * A quotation opened from a read-only demand (cancelled, decided, complete, or rejected): the same
 * details, every field read-only, a Read Only badge, the vendor's price for each line and any extra
 * items, and only Close (HS2-MKHVY9).
 */
export function QuoteViewDialog({ matrix: m, quotationId }: { matrix: QuoteMatrix; quotationId: string }): SafeHtml {
  const col = m.columns.find((c) => c.quotationId === quotationId);
  if (col === undefined) return <></>;
  const currency = col.currency ?? m.base;
  const priced = m.lineItems
    .map((line) => ({ line, cell: m.cells[line.id]?.[quotationId] }))
    .filter((row) => row.cell !== undefined);
  const extras = m.unmatched.filter((item) => item.quotationId === quotationId);
  const field = (name: string, label: string, value: string | null): SafeHtml =>
    value === null || value === '' ? <></> : <TextField type="text" name={name} label={label} value={value} readOnly />;
  return (
    <DialogSurface
      className="quote-view-dialog"
      label={`Quotation — ${col.vendorName}`}
      lightDismiss
      headerActions={
        <Badge
          tone="neutral"
          shape="pill"
          icon={<LucideIcon icon={Lock} name="" />}
          title="This demand can't be changed right now, so its quotations are view only."
        >
          Read Only
        </Badge>
      }
      rootAttributes={{ 'data-read-only': '', 'data-auto-open-dialog': '' }}
      footer={
        <Button
          tone="neutral"
          size="default"
          emphasis="primary"
          action="quote-dialog-cancel"
          rootAttributes={{ 'data-dialog-close': '' }}
        >
          Close
        </Button>
      }
    >
      <List gap="m">
        {field('q-vendor', 'Vendor', col.vendorName)}
        <Grid columns={2} gap="m" flex>
          {field('q-currency', 'Currency', currency)}
          {field('q-basis', 'Price Basis', col.priceBasis)}
        </Grid>
        {field('q-payment-terms', 'Payment Terms', col.paymentTerms)}
        <Grid columns={2} gap="m" flex>
          {field('q-date', 'Quote Date', col.quoteDate === null ? null : col.quoteDate.slice(0, 10))}
          {field('q-source', 'Source File', col.sourceFilename)}
        </Grid>
        <ListHeader label="Prices" />
        {priced.length === 0 ? (
          <Text tone="quiet" size="compact">
            This quotation has no prices matched to the demand's lines.
          </Text>
        ) : (
          <table class="t">
            <thead>
              <tr>
                <th>Line item</th>
                <th class="n">Unit price</th>
              </tr>
            </thead>
            <tbody>
              {priced.map(({ line, cell }) => (
                <tr data-key={line.id}>
                  <td>
                    {String(line.lineNumber)}. {line.description}
                  </td>
                  <td class="n">{formatCurrencyText(cell?.unitPrice ?? null, cell?.currency ?? currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {extras.length > 0
          ? [
              <ListHeader label="Other Items — Not on the Demand" />,
              <table class="t">
                <tbody>
                  {extras.map((item) => (
                    <tr data-key={item.quoteLineId}>
                      <td>{item.description ?? '—'}</td>
                      <td class="n">{formatCurrencyText(item.unitPrice, item.currency ?? currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>,
            ]
          : null}
      </List>
    </DialogSurface>
  );
}
